<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import _ from 'lodash'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { ConditionGroup, CriterionCondition, ExistingCriterionConditionDto } from '@/store/modules/searches/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

import CriteriaDisplayer from './CriteriaDisplayer.vue'
import FilterBuilder from './FilterBuilder.vue'

const props = withDefaults(defineProps<{
  internalOperator: string
  group: ConditionGroup
  groupIsGlobal?: boolean
  mode: string
  pmsiType?: PmsiTypeEnum
}>(), {
  groupIsGlobal: false,
  pmsiType: PmsiTypeEnum.MCO
})

const emit = defineEmits<{
  'clear-message': []
  'delete-criteria-group': [id: number | string]
}>()

const store = useStore()
const { t } = useI18n()

const editingCriterion = ref<ExistingCriterionConditionDto | null>(null)

const addCriteria = ({ type, value }: { type: string, value: string }): void => {
  let processedValue = value

  if (['presentMedicalAct', 'absentMedicalAct'].includes(type)) {
    // Acts are always in uppercase: AAFA001, AGMP* etc...
    processedValue = value.toUpperCase()
  }

  // check for duplicate
  if (props.group.criteriaList.find((criterion) => criterion.type === type && criterion.value === processedValue)) {
    return
  }

  emit('clear-message')
  store.commit('addAdvancedCriteria', {
    criteriaGroupId: props.group.id,
    type,
    value: processedValue
  })
  store.dispatch('refreshStayList', {})
}

const removeCriteria = ({ criteriaGroupId, criterion }: ExistingCriterionConditionDto): void => {
  emit('clear-message')
  store.commit('removeAdvancedCriteria', { criteriaGroupId, criterion })
  store.dispatch('refreshStayList', {})
  editingCriterion.value = null
}

const deleteCriteriaGroup = (): void => {
  if (!props.groupIsGlobal) {
    emit('clear-message')
    emit('delete-criteria-group', props.group.id)
    store.dispatch('refreshStayList', {})
  }
}

const startEditCriteria = (condition: ExistingCriterionConditionDto): void => {
  editingCriterion.value = editingCriterion.value?.criterion.id === condition.criterion.id
    ? null
    : _.cloneDeep(condition)
}

const replaceCriteria = (criterion: CriterionCondition): void => {
  emit('clear-message')

  store.commit('updateAdvancedCriteria', {
    criteriaGroupId: props.group.id,
    criterion,
  })

  store.dispatch('refreshStayList', {})
  editingCriterion.value = null
}
</script>

<template>
  <div>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col">
            {{
              t('filter.advancedSearch.criteriaList')
            }}{{ groupIsGlobal ? t('filter.advancedSearch.globalCriteria') : '' }} :
          </div>
          <div
            v-if="!groupIsGlobal"
            class="col-auto saved-search-icon"
            @click="() => deleteCriteriaGroup(group.id)"
          >
            <sancare-octicon
              name="x"
              :width="17"
              :height="20"
            />
          </div>
        </div>
        <criteria-displayer
          :internal-operator="groupIsGlobal ? 'ET' : internalOperator"
          :criteria-group="group"
          :display-grouped-criteria="groupIsGlobal"
          :is-global="groupIsGlobal"
          :mode="mode"
          :editing-criterion-id="editingCriterion?.criterion.id"
          @edit-criteria="startEditCriteria"
          @remove-criteria="removeCriteria"
        />
        <hr>
        <filter-builder
          :key="editingCriterion?.criterion?.id ?? 'new'"
          :is-global="groupIsGlobal"
          :mode="mode"
          :pmsi-type="pmsiType"
          :editing-criterion="editingCriterion"
          @add-criteria="addCriteria"
          @replace-criteria="replaceCriteria"
        />
      </div>
    </div>
  </div>
</template>
